export interface CogoXyzGithubPrPreviewInput {
  workspaceId: string
  repo: string
  prNumber: number
  createdBy?: string
}

export interface CogoXyzGithubPrContext {
  repo: string
  prNumber: number
  title: string
  url: string
  state: string
  branch: string
  baseBranch: string
  commitSha: string
  changedFiles: string[]
  uiRelatedFiles: string[]
}

function githubApiUrl(repo: string, path: string): string {
  return `https://api.github.com/repos/${repo.replace(/^\/+|\/+$/g, '')}${path}`
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function isUiRelatedFile(path: string): boolean {
  return /\.(tsx|jsx|vue|svelte|html|css|scss|mdx)$/.test(path) ||
    /(^|\/)(app|pages|routes|components|ui|styles|public)\//.test(path)
}

async function fetchJson(url: string): Promise<unknown> {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/vnd.github+json',
    },
  })
  if (!response.ok) {
    throw new Error(`GitHub read failed (${response.status}) for ${url}`)
  }
  return response.json() as Promise<unknown>
}

export async function fetchGithubPrContext(
  input: CogoXyzGithubPrPreviewInput,
): Promise<CogoXyzGithubPrContext> {
  const pr = readRecord(await fetchJson(githubApiUrl(input.repo, `/pulls/${input.prNumber}`)))
  const files = await fetchJson(githubApiUrl(input.repo, `/pulls/${input.prNumber}/files?per_page=100`))
  const changedFiles = Array.isArray(files)
    ? files.map((file) => readString(readRecord(file).filename)).filter(Boolean)
    : []

  const head = readRecord(pr.head)
  const base = readRecord(pr.base)
  return {
    repo: input.repo,
    prNumber: input.prNumber,
    title: readString(pr.title) || `${input.repo}#${input.prNumber}`,
    url: readString(pr.html_url),
    state: readString(pr.state),
    branch: readString(head.ref),
    baseBranch: readString(base.ref),
    commitSha: readString(head.sha),
    changedFiles,
    uiRelatedFiles: changedFiles.filter(isUiRelatedFile),
  }
}

export function githubPrContextToMarkdown(context: CogoXyzGithubPrContext): string {
  const files = context.changedFiles.length > 0
    ? context.changedFiles.map((file) => `- ${file}`).join('\n')
    : '- No changed files returned'
  const uiFiles = context.uiRelatedFiles.length > 0
    ? context.uiRelatedFiles.map((file) => `- ${file}`).join('\n')
    : '- No UI-related files detected'

  return [
    `# ${context.title}`,
    '',
    `Repository: ${context.repo}`,
    `PR: #${context.prNumber}`,
    `State: ${context.state || 'unknown'}`,
    `Branch: ${context.branch || 'unknown'} -> ${context.baseBranch || 'unknown'}`,
    `Commit: ${context.commitSha || 'unknown'}`,
    '',
    '## UI-related files',
    uiFiles,
    '',
    '## Changed files',
    files,
  ].join('\n')
}
